import { create } from "zustand";

/**
 * Pourquoi l'API est momentanément injoignable.
 *
 * Toutes produisent la même attente, mais appellent des gestes opposés : un déploiement se termine
 * tout seul, une connexion coupée demande une action de l'utilisateur. Désigner la mauvaise cause
 * envoie chercher un problème inexistant — soit patienter pour un déploiement imaginaire, soit
 * inspecter un réseau qui fonctionne très bien.
 *
 * D'où la troisième valeur : un échec de transport **ne dit pas** à lui seul qui est en cause. Il
 * signifie aussi bien « le réseau de l'utilisateur est tombé » que « le serveur ne répond plus » —
 * pendant le remplacement des conteneurs, l'API est injoignable et ne peut même pas servir sa page
 * de maintenance. Faute de pouvoir trancher, on ne blâme personne.
 */
export type ApiOutageCause =
  /** Le serveur a répondu 502/503 : déploiement ou incident côté API, reconnexion automatique. */
  | "maintenance"
  /** `navigator.onLine` est faux : l'appareil sait qu'il n'a plus de réseau, c'est sans ambiguïté. */
  | "offline"
  /** Aucune réponse alors que l'appareil se croit connecté : serveur injoignable, cause indécidable. */
  | "unreachable";

interface MaintenanceStore {
  /** `true` pendant qu'un déploiement API est en cours (503) ou une coupure réseau retentée. */
  isMaintenance: boolean;
  /** Renseigné tant que `isMaintenance` vaut `true`, pour que le bandeau dise la vraie cause. */
  cause: ApiOutageCause | null;
  setMaintenance: (active: boolean, cause?: ApiOutageCause) => void;
}

export const useMaintenanceStore = create<MaintenanceStore>((set) => ({
  isMaintenance: false,
  cause: null,
  // Évite un re-render inutile si l'état ne change pas (appelé souvent depuis la couche API).
  setMaintenance: (active, cause) =>
    set((state) => {
      const nextCause = active ? (cause ?? null) : null;
      return state.isMaintenance === active && state.cause === nextCause
        ? state
        : { isMaintenance: active, cause: nextCause };
    }),
}));
